import {
  CSSProperties,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';

const PopoverPanel: FC<IPopoverPanelProps> = ({
  children,
  isOpen,
  toggle,
  onClose,
  ignoreOutsideClick,
  className,
  role = 'dialog',
  style,
  ariaLabel,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isOpenRef = useRef(isOpen);

  isOpenRef.current = isOpen;

  // Handle outside click. The Popover component from
  // @headlessui/react package is a similar component
  // this information form popover but we can not
  // control the open/close state for elements outside
  // of popover component, ie: Google address autocomplete
  // (.pac-container) menu.
  const handleOutsideClick = useCallback(
    event => {
      if (!isOpenRef.current) {
        return;
      }

      if (!ref.current?.parentNode?.contains(event.target)) {
        if (!onClose || onClose(event)) {
          toggle(false);
        }
      }
    },
    [onClose, toggle],
  );

  useEffect(() => {
    if (ignoreOutsideClick || !isOpen) {
      return;
    }

    const timeoutId = setTimeout(() => {
      window.addEventListener('click', handleOutsideClick);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('click', handleOutsideClick);
    };
  }, [handleOutsideClick, ignoreOutsideClick, isOpen]);

  if (!isOpen) {
    return null;
  }

  const isDialog = role === 'dialog';

  return (
    <div
      aria-label={ariaLabel}
      {...(isDialog && { 'aria-modal': true })}
      className={className}
      ref={ref}
      role={role}
      style={style}
      tabIndex={-1}
    >
      {children}
    </div>
  );
};
interface IPopoverPanelProps {
  children: ReactNode;
  className?: string;
  isOpen: boolean;
  ignoreOutsideClick?: boolean;
  role?: string;
  style?: CSSProperties;
  ariaLabel?: string;
  onClose?: (_e: any) => boolean;
  toggle: (_e: any) => void;
}

export default PopoverPanel;
